import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { useReveal } from '../../hooks/useReveal';
import { Stethoscope, Compass, Magnet, Zap, TrendingUp } from 'lucide-react';

const STEPS = [
    {
        n: '01',
        Icon: Stethoscope,
        title: 'Diagnose',
        body: 'We start by mapping where leads are actually being lost — site, funnel, follow-up, ops. No work begins until the leak is named.',
    },
    {
        n: '02',
        Icon: Compass,
        title: 'Clarify',
        body: 'We translate the diagnosis into a focused scope: which system, what it must do, and what we explicitly will not build yet.',
    },
    {
        n: '03',
        Icon: Magnet,
        title: 'Capture',
        body: 'We design and ship the asset that gets visitors to identify themselves — site, landing page, quiz, calculator, qualifier.',
    },
    {
        n: '04',
        Icon: Zap,
        title: 'Respond',
        body: 'We wire the follow-up: AI receptionist, automated routing, calendar handoff, owner alerts. Replies inside two minutes, every time.',
    },
    {
        n: '05',
        Icon: TrendingUp,
        title: 'Improve',
        body: 'Tracking, attribution and weekly review. Every project ships with a dashboard and a feedback loop, so the system keeps compounding.',
    },
];

const ease = [0.22, 1, 0.36, 1];

export function HowWeWork() {
    const ref = useReveal();
    const pipelineRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: pipelineRef,
        offset: ['start 0.85', 'end 0.4'],
    });
    // Width of the cyan progress overlay on the dashed connector
    const lineProgress = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

    return (
        <section
            id="how-we-work"
            data-testid="how-we-work-section"
            className="relative section overflow-hidden"
            style={{ background: 'var(--bg-deep)' }}
        >
            <div className="absolute inset-0 grid-bg grid-bg-fade opacity-30" />
            <div className="atmosphere opacity-60" />

            <div className="shell relative">
                <div ref={ref} className="reveal max-w-3xl">
                    <p className="eyebrow">Method · The ClearAdvance framework</p>
                    <h2 className="mt-6 font-display font-semibold text-[2.6rem] sm:text-[3.6rem] lg:text-[4.4rem] leading-[1.02] tracking-[-0.04em] text-white">
                        Five steps from{' '}
                        <span className="text-gradient-cyan">leak</span>
                        {' '}to{' '}
                        <span className="text-gradient-cyan">compounding system.</span>
                    </h2>
                </div>

                {/* Pipeline visual */}
                <div ref={pipelineRef} className="mt-16 lg:mt-24 relative">
                    {/* Connecting line (desktop) */}
                    <div className="hidden lg:block absolute left-0 right-0 top-[64px] h-[2px] z-0">
                        {/* Dim base track */}
                        <div
                            className="absolute inset-0"
                            style={{
                                backgroundImage:
                                    'repeating-linear-gradient(to right, rgba(255,255,255,0.18) 0 6px, transparent 6px 12px)',
                            }}
                        />
                        {/* Animated cyan progress fill */}
                        <motion.div
                            className="absolute inset-y-0 left-0"
                            style={{
                                width: lineProgress,
                                background: 'linear-gradient(90deg, #06B6D4, #3B82F6, #8B5CF6)',
                                boxShadow: '0 0 12px rgba(6,182,212,0.6)',
                                borderRadius: 2,
                            }}
                        />
                    </div>

                    <ol className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 lg:gap-3">
                        {STEPS.map((s, i) => (
                            <Step key={s.n} {...s} idx={i} />
                        ))}
                    </ol>
                </div>
            </div>
        </section>
    );
}

function Step({ n, Icon, title, body, idx }) {
    return (
        <motion.li
            data-testid={`method-step-${idx}`}
            className="relative list-none group"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: idx * 0.12, ease }}
        >
            <motion.div
                whileInView={{ scale: [0.85, 1.04, 1] }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.9, delay: idx * 0.12 + 0.1, ease }}
                className="relative z-10 grid place-items-center w-[128px] h-[128px] mx-auto rounded-full transition-shadow duration-700 group-hover:shadow-[0_0_60px_-8px_rgba(6,182,212,0.5)]"
                style={{
                    background: 'radial-gradient(circle at 30% 30%, rgba(6,182,212,0.25), rgba(11,18,33,0.95) 70%)',
                    border: '1px solid rgba(6,182,212,0.30)',
                    boxShadow: '0 18px 40px -16px rgba(6,182,212,0.4), 0 0 0 6px var(--bg-deep)',
                }}
            >
                <div className="grid place-items-center w-[90px] h-[90px] rounded-full transition-transform duration-500 group-hover:scale-105"
                    style={{
                        background: 'linear-gradient(160deg, rgba(255,255,255,0.05), rgba(11,18,33,0.4))',
                        border: '1px solid rgba(255,255,255,0.10)',
                    }}>
                    <Icon className="w-7 h-7 text-cyan-400 transition-transform duration-500 group-hover:scale-110" strokeWidth={1.5} />
                </div>
                {/* Ambient ring pulse on hover */}
                <span
                    className="absolute inset-0 rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                        boxShadow: '0 0 0 1px rgba(6,182,212,0.55), 0 0 30px rgba(6,182,212,0.4)',
                    }}
                />
            </motion.div>
            <div className="mt-6 text-center">
                <p className="font-mono text-[10px] tracking-[0.24em] uppercase font-medium text-cyan-400">
                    Step {n}
                </p>
                <h3 className="mt-2 font-display font-semibold text-[1.3rem] tracking-[-0.025em] text-white">
                    {title}
                </h3>
                <p className="mt-3 text-[13.5px] leading-[1.6] text-slate-400 max-w-[260px] mx-auto">
                    {body}
                </p>
            </div>
        </motion.li>
    );
}
